import { useState, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Loader from "../Loader/Loader";
import { toast } from "react-toastify";
import axios from "axios";
import { BASE_URL, DAO_CONTRACT_ADDRESS } from "../../utils/constant";
import {
  getHeaders,
  handleError,
  stripHtml,
} from "../../utils/helper";
import { usePublicClient, useWriteContract } from "wagmi";
import { readContract } from "@wagmi/core";
import { DAO_ABI } from "../../Contract/DAO_ABI";
import { config } from "../../config";
import { useAppKitAccount } from "@reown/appkit/react";
import JoditEditor from "jodit-react";

const defaultProposal = {
  proposalId: 0,
  title: "",
  description: "",
  startTime: "",
  endTime: "",
};

const AddProposalModal = ({
  show,
  handleClose,
  mode = "create",
  data = {},
}) => {
  const isView = mode === "view";
  const isUpdate = mode === "update";
  const isCreate = mode === "create";

  const { writeContractAsync } = useWriteContract();
  const { address } = useAppKitAccount();
  const publicClient = usePublicClient();
  const [formData, setFormData] = useState(defaultProposal);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const editor = useRef(null);

  //Populate form data on open (for view/update)
  useEffect(() => {
    if (show) {
      if (isUpdate || isView) {
        setFormData({
          ...defaultProposal,
          ...data,
          startTime: data?.startTime
            ? new Date(data.startTime).toISOString().slice(0, 16)
            : "",
          endTime: data?.endTime
            ? new Date(data.endTime).toISOString().slice(0, 16)
            : "",
        });
      } else {
        setFormData(defaultProposal);
      }
      setErrors({});
    }
  }, [show, mode, data]);

  //Validation
  const validate = () => {
    const newErrors = {};
    const now = Date.now();
    const fiveMinutesLater = now + 5 * 60 * 1000;

    if (!formData.title.trim()) newErrors.title = "Title is required";
    else if (formData.title.length < 3 || formData.title.length > 100)
      newErrors.title = "Title must be between 3 and 100 characters";

    const plainDesc = stripHtml(formData.description).trim();
    if (!plainDesc) newErrors.description = "Description is required";
    else if (plainDesc.length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    } else if (plainDesc.length > 500) {
      newErrors.description = "Description not be more then 500 characters";
    }

    if (!formData.startTime) newErrors.startTime = "Start time is required";
    if (!formData.endTime) newErrors.endTime = "End time is required";

    const start = new Date(formData.startTime).getTime();
    const end = new Date(formData.endTime).getTime();

    // 4️⃣ Parse the dates safely
    const startDate = new Date(formData.startTime);
    const endDate = new Date(formData.endTime);

    // 5️⃣ Check for invalid date objects
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      toast.error("Please enter valid date and time");
      return false;
    }

    if (start < now) newErrors.startTime = "Start time cannot be in the past";
    else if (start < fiveMinutesLater)
      newErrors.startTime = "Start time must be at least 5 minutes later";

    if (end <= start)
      newErrors.endTime = "End time must be later than start time";

    console.log("newErrors", newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  console.log("errors", errors);

  //Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log("name", name, value);
    const now = Date.now();
    const fiveMinutesLater = now + 5 * 60 * 1000;

    // const trimmedValue = value.trim();
    const trimmedValue = value
    setFormData((prev) => ({ ...prev, [name]: trimmedValue }));
    const newErrors = {};

    // Skip validation for startTime and endTime
    if (name === "startTime" || name === "endTime") {
      const start = new Date(value).getTime();
      const end = new Date(value).getTime();

      if (start < now) newErrors.startTime = "Start time cannot be in the past";
      else if (start < fiveMinutesLater)
        newErrors.startTime = "Start time must be at least 5 minutes later";
     

      // if (end <= start)
      // Optionally clear any existing error for these fields
      setErrors((prev) => ({ ...prev, [name]: newErrors[name] }));
      return; // Exit early – no further validation
    }

    const upperCasedValue = name.charAt(0).toUpperCase() + name.slice(1);

    const validationRegex = /^[a-zA-Z][a-zA-Z0-9\s.,!?;:-]*$/;

    if (!trimmedValue) {
      newErrors[name] = `${upperCasedValue} is required`;
    } else if (!validationRegex.test(trimmedValue)) {
      newErrors[
        name
      ] = `${upperCasedValue} must start with a letter and can only include letters, numbers, spaces, dots, and common punctuation (e.g., ., !, ?, ;, :, -)`;
    }

    setErrors((prev) => ({ ...prev, [name]: newErrors[name] || "" }));
  };

  //Jodit Editor Config
  const handleDescriptionChange = (content) => {
    setFormData((prev) => ({ ...prev, description: content }));
    const plainText = stripHtml(content).trim();
    setErrors((prev) => ({
      ...prev,
      description: plainText ? "" : "Description is required",
    }));
  };

  const editorConfig = {
    placeholder: "Enter proposal description...",
    askBeforePasteHTML: false,
    defaultActionOnPaste: "insert_as_html",
    readonly: isView,
    allowAttributes: ["style", "class"],
    cleanHTML: {
      removeEmptyElements: false,
    },
  };

  // Handle form submit
  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        startTime: Math.floor(new Date(formData.startTime).getTime() / 1000),
        endTime: Math.floor(new Date(formData.endTime).getTime() / 1000),
      };

      const contractData = {
        abi: DAO_ABI,
        address: DAO_CONTRACT_ADDRESS,
        functionName: "createProposal",
        args: [
          payload.title,
          payload.description,
          payload.startTime,
          payload.endTime,
        ],
      };

      const proposalId = await readContract(config, {
        ...contractData,
        account: address,
      });

      const tx = await writeContractAsync(contractData);
      console.log("tx", tx);
      const receipt = await publicClient.waitForTransactionReceipt({
        hash: tx,
      });

      if (receipt?.status === "success") {
        const res = await axios.post(
          `${BASE_URL}/proposal/create`,
          { ...payload, proposalId: Number(proposalId) },
          {
            headers: getHeaders(),
          }
        );

        if (res?.data?.success) {
          toast.success("Proposal created successfully");
          handleClose(true);
        } else {
          toast.error(res?.data?.message || "Failed to create proposal");
        }
      }
    } catch (error) {
      console.error(error);
      const contractError = handleError(error?.cause?.data);
      if (contractError) {
        toast.error(contractError);
      } else {
        toast.error(
          error?.response?.data?.message || "Error creating proposal"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  //Render field
  const renderField = (label, name, type = "text", placeholder = "") => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        readOnly={isView}
        placeholder={placeholder}
        className={errors[name] ? "is-invalid" : ""}
      />
      {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
    </Form.Group>
  );

  return (
    <>
      {loading && <Loader />}
      <Modal
        show={show}
        onHide={() => {
          handleClose();
          setFormData(defaultProposal);
          setErrors({});
        }}
        centered
        className="modal-bg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isCreate && "Create Proposal"}
            {isUpdate && "Update Proposal"}
            {isView && "View Proposal"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {renderField("Title", "title", "text", "Enter title")}
            {/* <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <div className={errors.description ? "is-invalid" : ""}>
                <JoditEditor
                  ref={editor}
                  value={formData?.description}
                  config={editorConfig}
                  onBlur={handleDescriptionChange}
                />
              </div>
            </Form.Group> */}
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <div
                className={`jodit-wrapper ${
                  errors.description ? "is-invalid" : ""
                }`}
              >
                <JoditEditor
                  ref={editor}
                  value={formData?.description || ""}
                  config={editorConfig}
                  onBlur={handleDescriptionChange}
                  className="jodit-editor-custom"
                />
              </div>
              {errors.description && (
                <div className="invalid-feedback d-block">
                  {errors.description}
                </div>
              )}
            </Form.Group>

            {/* {errors.description && (
              <div className="invalid-feedback">{errors.description}</div>
            )} */}
            {renderField("Start Time", "startTime", "datetime-local")}
            {renderField("End Time", "endTime", "datetime-local")}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              handleClose();
              setFormData(defaultProposal);
              setErrors({});
            }}
          >
            Close
          </Button>
          {!isView && (
            <Button variant="primary" onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : isCreate ? "Create" : "Update"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddProposalModal;
